/**
 * Otto dog sound effects.
 *
 * Files expected at: /sounds/otto/{name}.mp3
 *
 * Sound catalogue (all to be generated via Gemini / AI audio):
 * ─────────────────────────────────────────────────────────────
 *  hey_otto      — Otto greeting sound / wake word confirmation (~1.0 s)
 *  bark          — single sharp dog bark (~0.4 s)
 *  bark_growl    — low aggressive growl ending in a bark (~1.0 s)
 *  yelp          — pain yelp / frightened cry (~0.5 s)
 *  whine         — soft whimper / settling whine (~1.0 s)
 *  sniff         — one audible dog sniff (~0.4 s)
 *  eat           — lapping / chewing sounds (~1.2 s)
 *  pant          — short panting breath, 2-3 cycles (~1.5 s)
 *  collar        — metal dog-tag collar jingle (~0.5 s)
 *  footstep_walk — soft paw-pad thud on ground (~0.3 s, one step)
 *  footstep_run  — fast paw thud, slightly harder (~0.2 s, one step)
 *  splash        — water splash / entry (~0.6 s)
 *  effort        — physical exertion grunt (jump / reborn) (~0.4 s)
 * ─────────────────────────────────────────────────────────────
 * Preload must be called after a user gesture to satisfy browser autoplay policy.
 */

export const OTTO_SOUNDS = [
  'hey_otto',
  'bark',
  'bark_growl',
  'yelp',
  'whine',
  'sniff',
  'eat',
  'pant',
  'collar',
  'footstep_walk',
  'footstep_run',
  'splash',
  'effort',
] as const;

export type OttoSound = (typeof OTTO_SOUNDS)[number];

// Pool size per sound — allows overlapping instances (e.g. rapid footsteps)
const POOL_SIZE = 3;

const pool: Partial<Record<OttoSound, HTMLAudioElement[]>> = {};
let preloaded = false;

/** Call once after a user gesture (e.g. first FAB click). */
export function preloadOttoSounds() {
  if (preloaded) return;
  preloaded = true;

  for (const name of OTTO_SOUNDS) {
    pool[name] = Array.from({ length: POOL_SIZE }, () => {
      const a = new Audio(`/sounds/otto/${name}.mp3`);
      a.preload = 'auto';
      a.volume = 0.5;
      return a;
    });
  }
}

/**
 * Stop all currently playing Otto sounds immediately.
 * Call this before starting a new animation so the previous sound doesn't bleed through.
 */
export function stopAllOttoSounds() {
  for (const sounds of Object.values(pool)) {
    for (const el of sounds as HTMLAudioElement[]) {
      if (!el.paused) {
        el.pause();
        el.currentTime = 0;
      }
    }
  }
}

/**
 * Play a named sound effect.
 * @param name    Sound name from OTTO_SOUNDS
 * @param volume  0.0 – 1.0 (default 0.5)
 */
export function playOttoSound(name: OttoSound, volume = 0.5) {
  const sounds = pool[name];
  if (!sounds?.length) return;

  // Pick the first idle element in the pool, or fall back to the first one
  const el = sounds.find((a) => a.paused) ?? sounds[0];
  el.volume = Math.max(0, Math.min(1, volume));
  el.currentTime = 0;
  el.play().catch(() => {
    // Swallow autoplay / missing-file errors silently
  });
}
